import { useState, type FormEvent } from "react";
import { useAuth } from "../auth/AuthContext";
import BrandMark from "../ui/BrandMark";
import { ArrowLeft, Lock } from "../ui/icons";
import "./Login.css";
import { t } from "../i18n";

/*
 * Staff sign-in. Wealthoak staff only: there is no sign-up, and accounts and
 * roles are managed in the admin panel. Password first, then the code from
 * the authenticator app.
 */
export default function Login() {
  const { step, signIn, verify, restart } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(e: FormEvent, action: () => Promise<void>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Sign-in failed. Try again."));
    } finally {
      setBusy(false);
    }
  }

  const mfa = step.kind === "mfa";

  return (
    <main className="crm-login">
      <section className="crm-login__card">
        <header className="crm-login__brand">
          <BrandMark size={40} />
          <div>
            <strong>{t("WealthOak CRM")}</strong>
            <small>{t("For the Wealthoak team")}</small>
          </div>
        </header>

        {!mfa ? (
          <form onSubmit={(e) => run(e, () => signIn(email.trim(), password))} noValidate>
            <h1>{t("Sign in")}</h1>
            <p className="crm-login__lede">{t("Use your work email and password.")}</p>
            <label className="crm-field">
              <span>{t("Work email")}</span>
              <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
            </label>
            <label className="crm-field">
              <span>{t("Password")}</span>
              <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </label>
            {error && <p className="crm-form-error" role="alert">{error}</p>}
            <button className="button button--primary crm-login__go" disabled={busy || !email || !password}>
              {busy ? t("Checking…") : t("Continue")}
            </button>
          </form>
        ) : (
          <form onSubmit={(e) => run(e, () => verify(code))} noValidate>
            <h1>{t("Two-factor code")}</h1>
            <p className="crm-login__lede">
              {t("Enter the six-digit code from your authenticator app for")} <strong>{step.email}</strong>.
            </p>
            <label className="crm-field">
              <span>{t("Code")}</span>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9 ]*"
                maxLength={7}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="crm-login__code num"
                autoFocus
              />
            </label>
            {error && <p className="crm-form-error" role="alert">{error}</p>}
            <button className="button button--primary crm-login__go" disabled={busy || code.replace(/\s/g, "").length !== 6}>
              {busy ? t("Verifying…") : t("Sign in")}
            </button>
            <button type="button" className="wo-link crm-login__back" onClick={restart}>
              <ArrowLeft size={14} /> {t("Use a different account")}
            </button>
          </form>
        )}

        <footer className="crm-login__foot">
          <Lock size={13} /> {t("Access is logged. Ask the team to add your staff account.")}
        </footer>
      </section>
    </main>
  );
}
